import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://s.techassembly.io';

export default function Login() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoExchanging, setSsoExchanging] = useState(false);
  const { login, register, exchangeSSOToken } = useAuth();
  const [searchParams] = useSearchParams();

  // Handle SSO token from URL fragment (#sso_token=...)
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/sso_token=([^&]+)/);
    if (match) {
      const token = decodeURIComponent(match[1]);
      setSsoExchanging(true);
      exchangeSSOToken(token)
        .catch((err) => setError(err.message || 'SSO sign-in failed'))
        .finally(() => setSsoExchanging(false));
      // Clean fragment
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
  }, [exchangeSSOToken]);

  // Handle OTP verification redirect
  useEffect(() => {
    const verifyUserId = searchParams.get('sso_verify');
    if (verifyUserId) {
      // Show OTP modal or redirect to verify page
      setOtpUserId(verifyUserId);
      setShowOtp(true);
    }
  }, [searchParams]);

  const [showOtp, setShowOtp] = useState(false);
  const [otpUserId, setOtpUserId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, fullName);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    setOtpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/sso/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: otpUserId, otp: otpCode }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      // Log in with returned token
      localStorage.setItem('token', data.data.token);
      window.location.href = '/';
    } catch (err: any) {
      setOtpError(err.message || 'Verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!otpUserId) return;
    try {
      await fetch(`${API_BASE}/api/auth/sso/verify/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: otpUserId }),
      });
      setOtpError('');
    } catch (err: any) {
      setOtpError(err.message);
    }
  };

  const startAuth0 = (connection?: string) => {
    const redirectUrl = `${window.location.origin}${window.location.pathname}`;
    const url = new URL(`${API_BASE}/api/auth/auth0/login`);
    url.searchParams.set('redirect_url', redirectUrl);
    url.searchParams.set('mode', 'fragment');
    if (connection) url.searchParams.set('connection', connection);
    window.location.href = url.toString();
  };

  if (showOtp && otpUserId) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.heading}>Verify your email</h1>
          <p style={styles.subtitle}>Enter the 6-digit code sent to your inbox</p>
          {otpError && <div style={styles.error}>{otpError}</div>}
          <form onSubmit={handleOtpSubmit} style={styles.form}>
            <input
              type="text"
              placeholder="6-digit code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              style={styles.input}
              maxLength={6}
              required
            />
            <button type="submit" disabled={otpLoading} style={styles.button}>
              {otpLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          <div style={styles.switch}>
            <button onClick={handleResendOtp} style={styles.linkBtn}>Resend code</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.heading}>TechAssembly</h1>
        <p style={styles.subtitle}>
          {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
        </p>

        {ssoExchanging && (
          <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>
            Signing you in...
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {/* Social login buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <button onClick={() => startAuth0('google-oauth2')} style={styles.socialBtn}>
            <GoogleIcon /> Continue with Google
          </button>
        </div>

        <div style={styles.divider}>
          <span style={styles.dividerText}>or</span>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={styles.input}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
            minLength={6}
          />
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div style={styles.switch}>
          {mode === 'login' ? (
            <>
              No account?{' '}
              <button onClick={() => setMode('register')} style={styles.linkBtn}>
                Register
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button onClick={() => setMode('login')} style={styles.linkBtn}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 8 }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z"/>
      <path fill="#34A853" d="M9 18a8.59 8.59 0 0 0 5.96-2.18l-2.92-2.26a5.43 5.43 0 0 1-8.08-2.85H1.1v2.33A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.96 10.71a5.36 5.36 0 0 1 0-3.42V4.96H1.1a9 9 0 0 0 0 8.08l2.86-2.33z"/>
      <path fill="#EA4335" d="M9 3.58a4.86 4.86 0 0 1 3.44 1.35l2.58-2.58A8.66 8.66 0 0 0 9 0 9 9 0 0 0 1.1 4.96l2.86 2.33A5.36 5.36 0 0 1 9 3.58z"/>
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    padding: '0 16px',
  },
  card: {
    background: '#fff',
    padding: 40,
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  heading: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 24,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  input: {
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    outline: 'none',
    fontFamily: 'inherit',
  },
  button: {
    padding: '10px',
    fontSize: 14,
    fontWeight: 500,
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    marginTop: 4,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  socialBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 12px',
    fontSize: 14,
    fontWeight: 500,
    background: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  error: {
    background: '#fef2f2',
    color: '#991b1b',
    padding: '10px 12px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 12,
  },
  switch: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 13,
    color: '#6b7280',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#111827',
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    fontSize: 13,
    textDecoration: 'underline',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '16px 0',
  },
  dividerText: {
    fontSize: 12,
    color: '#9ca3af',
    padding: '0 12px',
    background: '#fff',
    position: 'relative',
    zIndex: 1,
  },
};
